import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarPlus, Copy } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button, Dialog, Input } from '../components/ui.tsx'
import { del, get, post } from '../lib/api.ts'
import { fmtDateTime } from '../lib/format.ts'
import { t } from '../lib/i18n.ts'

interface Status { active: boolean; createdAt: string | null; lastUsedAt: string | null }

/** "Assinar no celular" (ADR 0023): a read-only iCalendar URL for the phone's calendar app, shown once, rotatable, revocable. */
export function AgendaFeedButton({ condoId }: { condoId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const q = useQuery({ queryKey: ['agenda-feed', condoId], queryFn: () => get<Status>(`/api/condos/${condoId}/agenda/feed`), enabled: Boolean(condoId && open) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['agenda-feed', condoId] })
  const create = useMutation({ mutationFn: () => post<{ url: string }>(`/api/condos/${condoId}/agenda/feed`), onSuccess: (r) => { setUrl(r.url); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const revoke = useMutation({ mutationFn: () => del(`/api/condos/${condoId}/agenda/feed`), onSuccess: () => { setUrl(null); toast.success(t('Endereço revogado')); invalidate() }, onError: (e) => toast.error((e as Error).message) })
  const copy = async () => {
    try { await navigator.clipboard.writeText(url!); toast.success(t('Endereço copiado')) } catch { toast.error(t('Não deu para copiar. Selecione o texto e copie.')) }
  }
  const s = q.data
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}><CalendarPlus className="size-4" />{' '}{t('Assinar no celular')}</Button>
      <Dialog open={open} onClose={() => { setOpen(false); setUrl(null) }} title={t('Agenda no calendário do celular')}>
        <div className="grid gap-3 text-[0.95rem]">
          <p className="text-tinta-2">{t('Um endereço só seu que o calendário do celular atualiza sozinho: reservas, assembleias, prazos, mudanças, prestadores e obras, como você os vê aqui.')}</p>
          {url ? (
            <>
              <div className="flex gap-2">
                <Input id="af-url" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
                <Button type="button" variant="outline" onClick={copy} aria-label={t('Copiar endereço')}><Copy className="size-4" /></Button>
              </div>
              <p className="text-sm text-tinta-2">{t('Guarde agora: ele aparece só desta vez. Quem tiver o endereço vê a sua agenda do condomínio, e o serviço do calendário guarda uma cópia dele; para invalidar, gere outro ou revogue.')}</p>
              <ul className="list-disc space-y-1 pl-5 text-sm text-tinta-2">
                <li><span className="font-semibold text-tinta">{t('iPhone:')}</span>{' '}{t('Ajustes → Apps → Calendário → Contas → Adicionar conta → Outra → Adicionar calendário assinado → cole o endereço.')}</li>
                <li><span className="font-semibold text-tinta">{t('Google Agenda:')}</span>{' '}{t('no computador, Outras agendas → + → Por URL → cole o endereço; ela aparece no celular.')}</li>
                <li><span className="font-semibold text-tinta">{t('Outlook:')}</span>{' '}{t('Adicionar calendário → Assinar da Web → cole o endereço.')}</li>
              </ul>
            </>
          ) : (
            <>
              {s?.active && <p className="text-sm text-tinta-2">{t('Endereço ativo desde')}{' '}{fmtDateTime(s.createdAt)}{s.lastUsedAt ? t(', usado pela última vez em {when}', { when: fmtDateTime(s.lastUsedAt) }) : t(', ainda não usado')}{t('. Gerar outro invalida o atual.')}</p>}
              <div className="flex flex-wrap justify-end gap-2">
                {s?.active && <Button variant="quiet" busy={revoke.isPending} onClick={() => revoke.mutate()}>{t('Revogar')}</Button>}
                <Button busy={create.isPending} onClick={() => create.mutate()}>{s?.active ? t('Gerar novo endereço') : t('Gerar endereço')}</Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </>
  )
}
